import { useContext, useMemo } from 'react'
import { PagesContext } from '@contexts/Pages/PagesContext'
import {
  getCMSApiClient,
  mergeRegions,
  formatPageResponse
} from '@utils/pageUtils'

export type PageMetaTypes = {
  title: string
  description: string
  keywords: string
  canonicals: []
}

export type PageTypes = {
  id: string
  meta: PageMetaTypes
  found: boolean
  content: any
}

export type PageInitialTypes = {
  pageIdOrSlug: string
  data?: {
    page: PageTypes
    globalPage: PageTypes
  }
}

const usePages = () => {
  const { state, actions, config, locale, componentMapping } =
    useContext(PagesContext)

  const api = getCMSApiClient(config, locale)

  const self: any = useMemo(() => {
    return {
      currentPage: state?.currentPage,

      componentMapping: componentMapping,

      init: async (
        pageInitial: PageInitialTypes | undefined
      ): Promise<PageTypes | undefined> => {
        let page: PageTypes | undefined = undefined

        if (pageInitial) {
          let globalPage: PageTypes | undefined = pageInitial.data?.globalPage
            ? pageInitial.data?.globalPage
            : self.findPageByPageIdOrSlug('global')
          page = pageInitial.data?.page
            ? pageInitial.data?.page
            : self.findPageByPageIdOrSlug(pageInitial.pageIdOrSlug)

          if (!page && globalPage) {
            const response = await api
              .getPageByIdOrSlug(pageInitial.pageIdOrSlug)
              .catch(() => undefined)
            page = formatPageResponse(response)
          } else if (!page && !globalPage) {
            const batchResponse = await Promise.all([
              api
                .getPageByIdOrSlug(pageInitial.pageIdOrSlug)
                .catch(() => undefined),
              api.getPageByIdOrSlug('global')
            ])
            page = formatPageResponse(batchResponse[0])
            globalPage = formatPageResponse(batchResponse[1])
          }

          if (!page) {
            page = Object.assign({}, globalPage)
            page.id = pageInitial.pageIdOrSlug
            page.content.id = pageInitial.pageIdOrSlug
            page.found = false
          }

          if (page && globalPage) {
            page.content.regions = mergeRegions(
              page.content.regions,
              globalPage.content.regions
            )

            actions.receivePage(page)
            actions.receivePage(globalPage)
          }
        }

        return Promise.resolve(page)
      },

      findPageByPageIdOrSlug(pageIdOrSlug: string): PageTypes | undefined {
        if (!state?.pages) {
          return undefined
        }
        const pages: Array<PageTypes | undefined> = Object.values(state?.pages)
        return pages.find((value) => !!value && value.id === pageIdOrSlug)
      }
    }
  }, [state])

  return self
}

export default usePages
